/**
 * "Bring me back here after I sign in", for costume pickup links only
 * (2026-09-25). A parent who scans a bag signed out used to be told to sign
 * in "then scan again", and landed on her home screen with the bag still in
 * her hand. The scan page now remembers its own address here, and
 * components/PickupReturnTrip.tsx (mounted in App.tsx) sends her straight back
 * once her account is active and she lands on home.
 *
 * localStorage, not sessionStorage: Safari and in-app browsers lose
 * sessionStorage across the sign-in round trip (lib/authSignInPolicy.ts records
 * that). Every read and write is wrapped, so a private window or blocked
 * storage simply means no return trip, never an error.
 *
 * NARROW ON PURPOSE. Only a pickup path is ever stored or returned, and it
 * expires after 15 minutes, so this can never become an open redirect or send
 * someone somewhere stale days later.
 */

const KEY = 'ldg:return-to:v1';
export const RETURN_TO_TTL_MS = 15 * 60 * 1000;

const PICKUP_PATH = /^\/(?:cp\/[A-Za-z0-9_-]{1,128}\/[A-Za-z0-9_-]{1,128}|costume-pickup\/[A-Za-z0-9_-]{1,128})$/;

/**
 * Any costume pickup route, loosely: the dancer route clamp in App.tsx and
 * the welcome tour (GlobalChrome.tsx) both step aside on these, because a
 * person standing at the desk with a bag must see the pickup card at once.
 */
export function isCostumePickupPath(path: string): boolean {
  return /^\/cp\/[^/]+\/[^/]+$/.test(path) || /^\/costume-pickup\/[^/]+$/.test(path);
}

/** True for the two costume pickup routes and nothing else. */
export function isReturnablePath(path: string): boolean {
  return PICKUP_PATH.test(path);
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function rememberReturnTo(path: string, now = Date.now()): void {
  if (!isReturnablePath(path)) return;
  try {
    storage()?.setItem(KEY, JSON.stringify({ path, at: now }));
  } catch {
    // Storage full or blocked: the parent scans again, as before.
  }
}

export function forgetReturnTo(): void {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

/** The remembered pickup path, once, and only if it is fresh. Always clears. */
export function takeReturnTo(now = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  forgetReturnTo();
  try {
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown };
    const path = typeof parsed.path === 'string' ? parsed.path : '';
    const at = typeof parsed.at === 'number' ? parsed.at : 0;
    if (!isReturnablePath(path) || !(now - at >= 0 && now - at <= RETURN_TO_TTL_MS)) return null;
    return path;
  } catch {
    return null;
  }
}
